import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiSend } from '../lib/api'
import type { Agent, Customer } from '../lib/types'

const SCHALTER: { key: 'traps' | 'syslog' | 'flows'; label: string; port: string }[] = [
  { key: 'traps', label: 'SNMP-Traps', port: 'UDP 162' },
  { key: 'syslog', label: 'Syslog', port: 'UDP 514' },
  { key: 'flows', label: 'Datenverkehr', port: 'UDP 2055 und 6343' },
]

/**
 * Die Empfänger eines Agents ein- und ausschalten. Jeder öffnet einen Port
 * im Kundennetz; deshalb sind alle aus, bis jemand sie ausdrücklich
 * einschaltet, und der Agent muss sie in seiner agent.env zusätzlich
 * erlauben (docs/sicherheit.md).
 */
export function AgentEmpfang({ agent, editable }: { agent: Agent; editable: boolean }) {
  const queryClient = useQueryClient()
  const [stand, setStand] = useState({
    traps: agent.receive_traps ?? false,
    syslog: agent.receive_syslog ?? false,
    flows: agent.receive_flows ?? false,
  })
  const speichern = useMutation({
    mutationFn: (neu: typeof stand) => apiSend('POST', `/agents/${agent.id}/receivers`, neu),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['agents'] }),
  })

  return (
    <fieldset className="empfang" disabled={!editable || speichern.isPending}>
      <legend className="sr-only">Empfang von {agent.name}</legend>
      {SCHALTER.map((s) => (
        <label key={s.key} title={s.port} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={stand[s.key]}
            onChange={(e) => {
              const neu = { ...stand, [s.key]: e.target.checked }
              setStand(neu)
              speichern.mutate(neu)
            }}
          />
          {s.label}
        </label>
      ))}
      {speichern.isError ? (
        <span style={{ color: 'var(--crit)', fontSize: 12 }}>{(speichern.error as Error).message}</span>
      ) : null}
    </fieldset>
  )
}

const AUFBEWAHRUNG = [0, 7, 30, 90]

/**
 * Wie lange ein Kunde Datenverkehr aufbewahrt. Ohne Aufbewahrung empfangen
 * seine Agents keine Flows, auch wenn der Schalter am Agent an ist.
 */
export function DatenverkehrAufbewahrung({ customer }: { customer: Customer }) {
  const queryClient = useQueryClient()
  const aktuell = customer.flow_retention_days ?? 0
  const [tage, setTage] = useState(aktuell)
  const speichern = useMutation({
    mutationFn: () => apiSend('PUT', `/customers/${customer.id}/datenverkehr`, { aufbewahrung_tage: tage }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['customers'] }),
  })
  return (
    <form
      className="card-body row"
      style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
      onSubmit={(e) => {
        e.preventDefault()
        speichern.mutate()
      }}
    >
      <div className="field">
        <label htmlFor="datenverkehr-aufbewahrung">Datenverkehr von {customer.name}</label>
        <select
          id="datenverkehr-aufbewahrung"
          className="input"
          value={tage}
          onChange={(e) => setTage(Number(e.target.value))}
        >
          {AUFBEWAHRUNG.map((t) => (
            <option key={t} value={t}>
              {t === 0 ? 'nicht speichern' : `${t} Tage aufbewahren`}
            </option>
          ))}
        </select>
      </div>
      <button type="submit" className="btn" disabled={tage === aktuell || speichern.isPending}>
        Aufbewahrung speichern
      </button>
      <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 240 }}>
        {tage === 0 && aktuell > 0
          ? 'Beim Abschalten wird der gespeicherte Datenverkehr mit der nächsten Bereinigung gelöscht.'
          : 'Gespeichert werden Minuten-Summen mit den größten Gesprächen, also auch Adressen von Endgeräten.'}
      </span>
      {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
    </form>
  )
}
